import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Warehouse layout ----
  listWarehouses() {
    return this.request('/warehouses');
  }

  listZones(warehouseId) {
    return this.request(`/warehouses/${warehouseId}/zones`);
  }

  createZone(warehouseId, { code, name, type }) {
    return this.request(`/warehouses/${warehouseId}/zones`, {
      method: 'POST',
      body: { code, name, type },
    });
  }

  listBins(warehouseId, { zone, aisle } = {}) {
    const qs = new URLSearchParams();
    if (zone) qs.set('zone', zone);
    if (aisle) qs.set('aisle', aisle);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/warehouses/${warehouseId}/bins${suffix}`);
  }

  batchGenerateBins(warehouseId, spec) {
    return this.request(`/warehouses/${warehouseId}/bins/batch`, { method: 'POST', body: spec });
  }

  warehouseStats(warehouseId) {
    return this.request(`/warehouses/${warehouseId}/stats`);
  }

  warehouseOccupancy(warehouseId) {
    return this.request(`/warehouses/${warehouseId}/occupancy`);
  }

  updateBin(binId, patch) {
    return this.request(`/bins/${binId}`, { method: 'PATCH', body: patch });
  }

  overrideBinStatus(binId, { status, reason, comment }) {
    return this.request(`/bins/${binId}/status`, { method: 'PATCH', body: { status, reason, comment } });
  }

  bulkUpdateBins(warehouseId, { binIds, status, merchantLock }) {
    return this.request(`/warehouses/${warehouseId}/bins/bulk`, {
      method: 'POST',
      body: { binIds, status, merchantLock },
    });
  }

  // ---- Inbound receiving ----
  listPurchaseOrders() {
    return this.request('/purchase-orders');
  }

  getPurchaseOrder(poId) {
    return this.request(`/purchase-orders/${poId}`);
  }

  scanPoItem(poId, itemId, qty = 1) {
    return this.request(`/purchase-orders/${poId}/items/${itemId}/scan`, {
      method: 'POST',
      body: { qty },
    });
  }

  applyPoItemQa(poId, itemId, payload) {
    return this.request(`/purchase-orders/${poId}/items/${itemId}`, { method: 'PATCH', body: payload });
  }

  reconcilePurchaseOrder(poId) {
    return this.request(`/purchase-orders/${poId}/receive`, { method: 'POST' });
  }

  // ---- Directed putaway / movement tasks ----
  listMovementTasks({ type } = {}) {
    const suffix = type ? `?type=${encodeURIComponent(type)}` : '';
    return this.request(`/movement-tasks${suffix}`);
  }

  completeMovementTask(taskId, scans) {
    return this.request(`/movement-tasks/${taskId}/complete`, { method: 'POST', body: scans });
  }

  overrideMovementTarget(taskId, { altBinCoordinate, reason }) {
    return this.request(`/movement-tasks/${taskId}/override`, {
      method: 'POST',
      body: { altBinCoordinate, reason },
    });
  }

  postponeMovementTask(taskId) {
    return this.request(`/movement-tasks/${taskId}/postpone`, { method: 'POST' });
  }

  // ---- Replenishment & min-max planner ----
  getReplenishment() {
    return this.request('/replenishment');
  }

  saveReplenishmentRule(rule) {
    return this.request('/replenishment/rules', { method: 'POST', body: rule });
  }

  simulatePick(ruleId, qty) {
    return this.request(`/replenishment/rules/${ruleId}/simulate-pick`, { method: 'POST', body: { qty } });
  }

  claimReplenishmentTask(taskId) {
    return this.request(`/replenishment/tasks/${taskId}/claim`, { method: 'POST' });
  }

  executeReplenishmentTask(taskId, payload) {
    return this.request(`/replenishment/tasks/${taskId}/execute`, { method: 'POST', body: payload });
  }

  // ---- Cycle counting ----
  listCycleCounts() {
    return this.request('/cycle-counts');
  }

  getCycleCount(sheetId) {
    return this.request(`/cycle-counts/${sheetId}`);
  }

  generateCycleCount(payload) {
    return this.request('/cycle-counts/generate', { method: 'POST', body: payload });
  }

  setCycleCountLine(sheetId, lineId, counted) {
    return this.request(`/cycle-counts/${sheetId}/lines/${lineId}`, { method: 'PATCH', body: { counted } });
  }

  submitCycleCount(sheetId) {
    return this.request(`/cycle-counts/${sheetId}/submit`, { method: 'POST' });
  }

  approveCycleCount(sheetId, reason) {
    return this.request(`/cycle-counts/${sheetId}/approve`, { method: 'POST', body: { reason } });
  }

  // ---- Bin transfers & adjustments ----
  getInventory() {
    return this.request('/inventory');
  }

  binTransfer(payload) {
    return this.request('/inventory/transfer', { method: 'POST', body: payload });
  }

  stockAdjustment(payload) {
    return this.request('/inventory/adjustments', { method: 'POST', body: payload });
  }

  approveAdjustment(movementId) {
    return this.request(`/inventory/adjustments/${movementId}/approve`, { method: 'POST' });
  }

  rejectAdjustment(movementId) {
    return this.request(`/inventory/adjustments/${movementId}/reject`, { method: 'POST' });
  }

  // ---- Channel integrations ----
  getChannels() {
    return this.request('/channels');
  }

  saveChannelConfig(connectorKey, payload) {
    return this.request(`/channels/${connectorKey}/config`, { method: 'POST', body: payload });
  }

  disconnectChannel(integrationId) {
    return this.request(`/channels/integrations/${integrationId}/disconnect`, { method: 'POST' });
  }

  // ---- Merchant store OAuth ----
  getMerchantStores() {
    return this.request('/merchant/stores');
  }

  beginStoreConnect(connector) {
    return this.request(`/merchant/stores/connect/${connector}`, { method: 'POST' });
  }

  merchantStoreCallback({ code, state }) {
    return this.request('/merchant/stores/callback', { method: 'POST', body: { code, state } });
  }

  renewMerchantStore(id) {
    return this.request(`/merchant/stores/${id}/renew`, { method: 'POST' });
  }

  disconnectMerchantStore(id) {
    return this.request(`/merchant/stores/${id}/disconnect`, { method: 'POST' });
  }

  // ---- Courier configuration ----
  getCouriers() {
    return this.request('/couriers');
  }

  saveCourierConfig(key, payload) {
    return this.request(`/couriers/${key}/config`, { method: 'POST', body: payload });
  }

  testCourier(key, payload) {
    return this.request(`/couriers/${key}/test`, { method: 'POST', body: payload });
  }

  getCourierZones() {
    return this.request('/couriers/zones');
  }

  createCourierZone(payload) {
    return this.request('/couriers/zones', { method: 'POST', body: payload });
  }

  updateCourierZone(id, payload) {
    return this.request(`/couriers/zones/${id}`, { method: 'PUT', body: payload });
  }

  deleteCourierZone(id) {
    return this.request(`/couriers/zones/${id}`, { method: 'DELETE' });
  }

  saveCourierRates(key, payload) {
    return this.request(`/couriers/${key}/rates`, { method: 'POST', body: payload });
  }

  // ---- Order routing rules ----
  getRoutingRules() {
    return this.request('/routing-rules');
  }

  createRoutingRule(payload) {
    return this.request('/routing-rules', { method: 'POST', body: payload });
  }

  updateRoutingRule(id, payload) {
    return this.request(`/routing-rules/${id}`, { method: 'PUT', body: payload });
  }

  deleteRoutingRule(id) {
    return this.request(`/routing-rules/${id}`, { method: 'DELETE' });
  }

  moveRoutingRule(id, direction) {
    return this.request(`/routing-rules/${id}/move`, { method: 'POST', body: { direction } });
  }

  simulateRouting(payload) {
    return this.request('/routing-rules/simulate', { method: 'POST', body: payload });
  }

  // ---- Wave & batch picking ----
  getPendingOrders({ carrier, zone } = {}) {
    const qs = new URLSearchParams();
    if (carrier && carrier !== 'ALL') qs.set('carrier', carrier);
    if (zone && zone !== 'ALL') qs.set('zone', zone);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/picking/pending-orders${suffix}`);
  }

  getWaves() {
    return this.request('/picking/waves');
  }

  createWave(payload) {
    return this.request('/picking/waves', { method: 'POST', body: payload });
  }

  assignWavePicker(id, picker) {
    return this.request(`/picking/waves/${id}/assign`, { method: 'PATCH', body: { picker } });
  }

  releaseWave(id) {
    return this.request(`/picking/waves/${id}/release`, { method: 'POST' });
  }

  pickWave(id, lines) {
    return this.request(`/picking/waves/${id}/pick`, { method: 'POST', body: { lines } });
  }

  // ---- Pick queue execution ----
  getPickJobs() {
    return this.request('/pick-jobs');
  }

  getPickJob(id) {
    return this.request(`/pick-jobs/${id}`);
  }

  startPickJob(id, tote) {
    return this.request(`/pick-jobs/${id}/start`, { method: 'POST', body: { tote } });
  }

  confirmPickStep(id, stepId, payload) {
    return this.request(`/pick-jobs/${id}/steps/${stepId}/confirm`, { method: 'POST', body: payload });
  }

  flagShortPick(id, stepId, payload) {
    return this.request(`/pick-jobs/${id}/steps/${stepId}/short`, { method: 'POST', body: payload });
  }

  flagDamagedPick(id, stepId, payload) {
    return this.request(`/pick-jobs/${id}/steps/${stepId}/damage`, { method: 'POST', body: payload });
  }

  skipPickStep(id, stepId) {
    return this.request(`/pick-jobs/${id}/steps/${stepId}/skip`, { method: 'POST' });
  }

  finishPickJob(id) {
    return this.request(`/pick-jobs/${id}/finish`, { method: 'POST' });
  }

  // ---- Packing station ----
  scanPackTote(tote) {
    return this.request('/packing/scan', { method: 'POST', body: { tote } });
  }

  scanPackItem(orderId, sku) {
    return this.request(`/packing/orders/${orderId}/scan-item`, { method: 'POST', body: { sku } });
  }

  confirmPack(orderId, payload) {
    return this.request(`/packing/orders/${orderId}/confirm`, { method: 'POST', body: payload });
  }

  // ---- Order fulfilment queue ----
  getFulfilmentOrders() {
    return this.request('/fulfilment/orders');
  }

  getFulfilmentOrder(id) {
    return this.request(`/fulfilment/orders/${id}`);
  }

  processFulfilmentOrder(id) {
    return this.request(`/fulfilment/orders/${id}/process`, { method: 'POST' });
  }

  verifyFulfilmentItem(id, itemId, verified) {
    return this.request(`/fulfilment/orders/${id}/items/${itemId}/verify`, { method: 'PATCH', body: { verified } });
  }

  holdFulfilmentOrders(ids, reason, comments) {
    return this.request('/fulfilment/orders/hold', { method: 'POST', body: { ids, reason, comments } });
  }

  releaseFulfilmentOrders(ids) {
    return this.request('/fulfilment/orders/release', { method: 'POST', body: { ids } });
  }

  allocateFulfilmentOrder(id, node) {
    return this.request(`/fulfilment/orders/${id}/allocate`, { method: 'POST', body: { node } });
  }

  confirmFulfilmentPack(id) {
    return this.request(`/fulfilment/orders/${id}/confirm-pack`, { method: 'POST' });
  }

  // ---- Returns intake ----
  getReturns() {
    return this.request('/returns');
  }

  lookupReturn(tracking) {
    return this.request('/returns/lookup', { method: 'POST', body: { tracking } });
  }

  scanReturnItem(id, sku) {
    return this.request(`/returns/${id}/scan-item`, { method: 'POST', body: { sku } });
  }

  gradeReturn(id, payload) {
    return this.request(`/returns/${id}/grade`, { method: 'PATCH', body: payload });
  }

  generateReturnSticker(id) {
    return this.request(`/returns/${id}/sticker`, { method: 'POST' });
  }

  confirmReturn(id) {
    return this.request(`/returns/${id}/confirm`, { method: 'POST' });
  }

  // ---- Country operational profiles ----
  getCountries() {
    return this.request('/countries');
  }

  createCountry(payload) {
    return this.request('/countries', { method: 'POST', body: payload });
  }

  updateCountry(id, payload) {
    return this.request(`/countries/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Multi-currency & regional tax ----
  getTaxCurrency() {
    return this.request('/tax-currency');
  }

  saveExchangeRate(payload) {
    return this.request('/tax-currency/rates', { method: 'POST', body: payload });
  }

  syncExchangeRate(id) {
    return this.request(`/tax-currency/rates/${id}/sync`, { method: 'POST' });
  }

  saveTaxRule(payload) {
    return this.request('/tax-currency/tax-rules', { method: 'POST', body: payload });
  }

  calculateTax(payload) {
    return this.request('/tax-currency/calculate', { method: 'POST', body: payload });
  }

  // ---- Client billing rules ----
  getBillingRules() {
    return this.request('/billing-rules');
  }

  applyBillingRules(id, payload) {
    return this.request(`/billing-rules/${id}`, { method: 'PUT', body: payload });
  }

  getBillingTariffs() {
    return this.request('/billing-rules/tariffs');
  }

  createBillingTariff(payload) {
    return this.request('/billing-rules/tariffs', { method: 'POST', body: payload });
  }

  // ---- Courier remittance ----
  getRemittanceBatches() {
    return this.request('/remittance/batches');
  }

  checkRemittanceBatch(batchRef) {
    return this.request('/remittance/check', { method: 'POST', body: { batchRef } });
  }

  previewRemittance(payload) {
    return this.request('/remittance/preview', { method: 'POST', body: payload });
  }

  reconcileRemittance(payload) {
    return this.request('/remittance/reconcile', { method: 'POST', body: payload });
  }

  // ---- COD discrepancy worklist ----
  getDiscrepancies() {
    return this.request('/discrepancies');
  }

  getDiscrepancy(id) {
    return this.request(`/discrepancies/${id}`);
  }

  resolveDiscrepancy(id, payload) {
    return this.request(`/discrepancies/${id}/resolve`, { method: 'POST', body: payload });
  }

  bulkResolveDiscrepancyMatches() {
    return this.request('/discrepancies/bulk-resolve-matches', { method: 'POST' });
  }

  // ---- Invoice generation ----
  getInvoiceClients() {
    return this.request('/invoices/clients');
  }

  getInvoices() {
    return this.request('/invoices');
  }

  generateInvoice(payload) {
    return this.request('/invoices/generate', { method: 'POST', body: payload });
  }

  validateInvoice(id) {
    return this.request(`/invoices/${id}/validate`, { method: 'POST' });
  }

  settleInvoice(id) {
    return this.request(`/invoices/${id}/settle`, { method: 'POST' });
  }

  issueInvoice(id) {
    return this.request(`/invoices/${id}/issue`, { method: 'POST' });
  }

  // ---- RTS / carrier claims ----
  getRtsLedger() {
    return this.request('/rts');
  }

  fileRtsClaim(parcelId, payload) {
    return this.request(`/rts/${parcelId}/claim`, { method: 'POST', body: payload });
  }

  recoverRtsClaim(parcelId) {
    return this.request(`/rts/${parcelId}/recover`, { method: 'POST' });
  }

  // ---- Merchant order dashboard (tenant-isolated, PII-masked) ----
  getMerchantOrders(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return this.request(`/merchant-orders${qs ? `?${qs}` : ''}`);
  }

  getMerchantOrder(id) {
    return this.request(`/merchant-orders/${id}`);
  }

  updateMerchantOrderAddress(id, address) {
    return this.request(`/merchant-orders/${id}/address`, { method: 'PATCH', body: { address } });
  }

  releaseMerchantOrderHold(id) {
    return this.request(`/merchant-orders/${id}/release-hold`, { method: 'POST' });
  }

  cancelMerchantOrder(id) {
    return this.request(`/merchant-orders/${id}/cancel`, { method: 'POST' });
  }

  // ---- Merchant COD collections (tenant-isolated) ----
  getCodCollections() {
    return this.request('/cod-collections');
  }

  getCodBatchOrders(batchId) {
    return this.request(`/cod-collections/batches/${batchId}/orders`);
  }

  // ---- Merchant portal dashboard (tenant-isolated) ----
  getMerchantDashboard() {
    return this.request('/merchant-dashboard');
  }

  // ---- Regional market performance (tenant-isolated) ----
  getRegionalPerformance() {
    return this.request('/regional-performance');
  }

  auditRegionalMerchant(id) {
    return this.request(`/regional-performance/ledger/${id}/audit`, { method: 'POST' });
  }

  auditAllRegional() {
    return this.request('/regional-performance/audit-all', { method: 'POST' });
  }

  // ---- Integration sync & API monitor ----
  getSyncLogs(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return this.request(`/sync-logs${qs ? `?${qs}` : ''}`);
  }

  getSyncLog(id) {
    return this.request(`/sync-logs/${id}`);
  }

  retrySyncLog(id) {
    return this.request(`/sync-logs/${id}/retry`, { method: 'POST' });
  }

  // ---- Cross-border logistics ----
  getCrossBorderShipments(exception) {
    return this.request(`/cross-border${exception ? `?exception=${encodeURIComponent(exception)}` : ''}`);
  }

  uploadCrossBorderDocument(id, payload) {
    return this.request(`/cross-border/${id}/documents`, { method: 'POST', body: payload });
  }

  setCrossBorderHsCode(id, hsCode) {
    return this.request(`/cross-border/${id}/hs-code`, { method: 'POST', body: { hsCode } });
  }
}

export default new ApiService();
